import React, { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Stack,
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import {
  Inventory as InventoryIcon,
  Dashboard as DashboardIcon,
  LocalShipping as SuppliersIcon,
  Description as EntriesIcon,
  Folder as DocumentsIcon,
} from "@mui/icons-material";
import { SelectClientBanner } from "@/components";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { layoutDimensions } from "@/utils/responsive";
import { tabSx } from "@/utils/customs-helpers";
import { tokens } from "@/tokens";
import { CUSTOMS_ERP_TABS, CUSTOMS_ERP_TAB_KEYS } from "@/constants";
import { useFilteredTabs } from "@/hooks/useFilteredTabs";

const TAB_ICON_MAP: Record<string, React.ReactElement> = {
  dashboard: <DashboardIcon sx={{ fontSize: 18 }} />,
  products: <InventoryIcon sx={{ fontSize: 18 }} />,
  suppliers: <SuppliersIcon sx={{ fontSize: 18 }} />,
  entries: <EntriesIcon sx={{ fontSize: 18 }} />,
  documents: <DocumentsIcon sx={{ fontSize: 18 }} />,
};

const CustomsERP: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const location = useLocation();
  const navigate = useNavigate();
  const filteredTabs = useFilteredTabs(CUSTOMS_ERP_TABS, CUSTOMS_ERP_TAB_KEYS, "CUSTOMS_ERP");

  const activeTab = useMemo(() => {
    const currentPath = location.pathname.split("/").pop() || "";
    const idx = filteredTabs.findIndex((tab) => tab.link === currentPath);
    return idx === -1 ? 0 : idx;
  }, [location.pathname, filteredTabs]);

  useEffect(() => {
    const currentPath = location.pathname.split("/").pop() || "";
    const found = filteredTabs.some((tab) => tab.link === currentPath);
    if (!found && filteredTabs.length > 0) {
      navigate(`/customs-erp/${filteredTabs[0].link}`, { replace: true });
    }
  }, [location.pathname, filteredTabs, navigate]);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = filteredTabs[newValue];
    if (!tab) return;
    navigate(`/customs-erp/${tab.link}`);
  };

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return <SelectClientBanner />;
  }

  const currentTabLink = filteredTabs[activeTab]?.link;
  const isFullHeightTab = currentTabLink !== "dashboard";

  return (
    <Stack
      spacing={0}
      sx={{
        // Use negative margins that match the layout padding to go edge-to-edge
        margin: {
          xs: `calc(-1 * ${layoutDimensions.layoutPaddingSmall}) calc(-1 * ${layoutDimensions.layoutPaddingMedium}) 0`,
          md: `calc(-1 * ${layoutDimensions.layoutPaddingMedium}) calc(-1 * ${layoutDimensions.layoutPaddingLarge}) 0`
        },
        width: {
          xs: `calc(100% + 2 * ${layoutDimensions.layoutPaddingMedium})`,
          md: `calc(100% + 2 * ${layoutDimensions.layoutPaddingLarge})`
        },
        // Always constrain to viewport so every tab (including dashboard) has a scroll boundary
        height: {
          xs: `calc(100vh - ${layoutDimensions.layoutPaddingSmall} - ${layoutDimensions.layoutPaddingSmall})`,
          md: `calc(100vh - ${layoutDimensions.layoutPaddingMedium} - ${layoutDimensions.layoutPaddingMedium})`,
        },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Main Tabs - Sticky at top */}
      <Box sx={{
        borderBottom: '1px solid #e2e5e9',
        margin: 0,
        padding: 0,
        width: '100%',
        bgcolor: tokens.bg.muted,
        flexShrink: 0, // Don't shrink the tab bar
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 48, px: { xs: 1.5, md: 3 }, margin: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 48,
              maxWidth: '100%',
              '& .MuiTabs-indicator': {
                backgroundColor: '#0a0a0f',
                height: 2,
                bottom: 0,
              },
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3,
              },
            }}
          >
            {filteredTabs.map((tab) => (
              <Tab
                key={tab.link}
                label={tab.text}
                icon={TAB_ICON_MAP[tab.link]}
                iconPosition="start"
                sx={{ ...tabSx, gap: 0.75 }}
              />
            ))}
          </Tabs>
        </Stack>
      </Box>

      {/* Tab Content - always fills remaining height */}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        // Full-height tabs handle their own scroll; dashboard scrolls here
        overflow: isFullHeightTab ? 'hidden' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        boxSizing: 'border-box',
        pr: { xs: 3, md: 5 },
      }}>
        <Outlet />
      </Box>
    </Stack>
  );
};

export default CustomsERP;
